import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface BillingInfo {
  billingPeriod: {
    id: number;
    label: string;
    year: number;
    deadline: string;
    daysUntilDeadline: number;
  };
  bowlerCount: number;
  pricePerBowler: number;
  grossAmount: number;
  totalPaid: number;
  currentDue: number;
  isOverdue: boolean;
}

const PERIOD_KEY: Record<string, string> = {
  may: 'admin.billing.periodMay',
  september: 'admin.billing.periodSeptember',
  december: 'admin.billing.periodDecember',
};

const AdminBillingCheckout: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [error, setError] = useState('');
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const fetchBilling = async () => {
      try {
        const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
        const res = await fetch(`${apiUrl}/admin/billing/current`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (res.ok) {
          const data = await res.json();
          setBilling(data);
        } else {
          setError('Failed to load billing information.');
        }
      } catch {
        setError('Failed to load billing information.');
      } finally {
        setLoading(false);
      }
    };
    fetchBilling();
  }, []);

  const handleCheckout = async () => {
    setProcessingCheckout(true);
    setError('');
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/admin/billing/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      if (data.success && data.stripeUrl) {
        window.location.href = data.stripeUrl;
      } else {
        setError(data.error || 'Failed to initiate payment.');
        setProcessingCheckout(false);
      }
    } catch {
      setError('Failed to initiate payment.');
      setProcessingCheckout(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-yellow-400 mx-auto mb-4" />
          <p className="text-purple-200">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-5xl mb-3">💳</div>
          <h1 className="text-3xl font-bold text-white">{t('admin.billing.checkoutTitle')}</h1>
          <p className="text-purple-200 mt-2">{t('admin.billing.checkoutDescription')}</p>
        </div>

        {/* Bill Card */}
        {billing && (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 mb-6">
            {/* Period / deadline */}
            <div className={`flex items-center justify-between mb-5 pb-4 border-b border-white/10`}>
              <div>
                <p className="text-purple-300 text-sm uppercase tracking-wide">{t('admin.billing.currentBill')}</p>
                <p className="text-white font-semibold">
                  {t('admin.billing.dueBy', { date: t(PERIOD_KEY[billing.billingPeriod.label] || 'admin.billing.periodDecember') })}
                  {' '}({billing.billingPeriod.year})
                </p>
              </div>
              {billing.isOverdue ? (
                <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">{t('admin.billing.overdueWarning')}</span>
              ) : (
                <span className="text-purple-300 text-sm">
                  {billing.billingPeriod.daysUntilDeadline > 0
                    ? `${billing.billingPeriod.daysUntilDeadline} days left`
                    : t('admin.billing.overdueWarning')}
                </span>
              )}
            </div>

            {/* Line items */}
            <div className="space-y-3 mb-5">
              <div className="flex justify-between text-sm">
                <span className="text-purple-200">{t('admin.billing.checkoutBowlers', { count: billing.bowlerCount })}</span>
                <span className="text-white">{t('admin.billing.checkoutPricePerBowler', { price: billing.pricePerBowler })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-purple-200">{t('admin.billing.checkoutAlreadyPaid')}</span>
                <span className="text-green-400">- ${billing.totalPaid.toFixed(2)}</span>
              </div>
              <div className="border-t border-white/20 pt-3 flex justify-between font-bold text-lg">
                <span className="text-white">{t('admin.billing.checkoutAmountDue')}</span>
                <span className="text-yellow-300">${billing.currentDue.toFixed(2)}</span>
              </div>
            </div>

            <p className="text-purple-300 text-xs text-center">{t('admin.billing.checkoutNote')}</p>
          </div>
        )}

        {/* Info toggle */}
        <div className="mb-6">
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="flex items-center gap-2 text-purple-300 hover:text-white text-sm transition"
          >
            <span className="w-5 h-5 rounded-full border border-purple-400 flex items-center justify-center text-xs font-bold">i</span>
            {t('admin.billing.infoTitle')}
            <span>{showInfo ? '▲' : '▼'}</span>
          </button>
          {showInfo && (
            <div className="mt-3 bg-white/5 rounded-lg p-4 border border-white/10 space-y-2 text-sm text-purple-200">
              <p>{t('admin.billing.infoLine1', { price: billing?.pricePerBowler ?? 8 })}</p>
              <p>{t('admin.billing.infoLine5')}</p>
              <p>{t('admin.billing.infoLine2')}</p>
              <p>{t('admin.billing.infoLine3')}</p>
              <p>{t('admin.billing.infoLine4')}</p>
              
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-400 rounded-lg p-3 mb-4 text-red-200 text-sm">{error}</div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleCheckout}
            disabled={processingCheckout || !billing || billing.currentDue <= 0}
            className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-4 rounded-xl text-lg transition duration-200 disabled:cursor-not-allowed"
          >
            {processingCheckout
              ? t('admin.billing.checkoutProcessing')
              : t('admin.billing.checkoutButton')}
          </button>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="w-full bg-white/10 hover:bg-white/20 text-white font-medium py-3 rounded-xl transition duration-200"
          >
            {t('admin.billing.checkoutCancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminBillingCheckout;
